import { createFileRoute } from '@tanstack/react-router';
import { AlertTriangle, Box } from 'lucide-react';
import z from 'zod';

import { remedyV3RelatedObjectsQueryOptions, sourceCodeV3QueryOptions } from '@/api/endpoints/remedy';
import { ObjectRemedyPageSkeleton } from '@/components/features/code-remedy/ObjectRemedyPageSkeleton';
import { buildScanObjectMap, orderObjects } from '@/components/features/code-remedy/utils';
import { useStoredRemedyChatOpen } from '@/features/code-remedy/hooks/use-remedy-chat-preference';
import { ObjectRemedyPage } from '@/features/code-remedy/ObjectRemedyPage';
import { ObjectRemediationStoreProvider } from '@/features/code-remedy/providers/ObjectRemediationStoreProvider';
import { extractHTTPError } from '@/utils/http-client';
import { safeInvoke } from '@/utils/safe-invoke';

export const Route = createFileRoute('/_protected/t/$tenantId/code-modernization/$remedyId/$objectId')({
  validateSearch: z.object({
    obj: z.string().optional(),
  }),
  loader: async ({ context: { queryClient }, params: { remedyId, objectId } }) => {
    const { data, error } = await safeInvoke(
      queryClient.ensureQueryData(remedyV3RelatedObjectsQueryOptions({ remedy_id: remedyId, object_id: objectId })),
    );

    const objects = orderObjects(data?.objects ?? [], objectId);

    const parentObject = objects.find((object) => object.object_id === objectId);
    const scanObjectMap = buildScanObjectMap(data?.scans ?? []);

    objects.forEach((object) => {
      queryClient.prefetchQuery(sourceCodeV3QueryOptions({ remedy_id: remedyId, object_id: object.object_id }));
    });

    return {
      parentObject,
      scanObjectMap,
      objects,
      errorMessage: error ? extractHTTPError(error, 'An unknown error occurred') : undefined,
    };
  },
  pendingComponent: () => <ObjectRemedyPageSkeleton />,
  component: CodeRemedyObjectRoute,
});

function CodeRemedyObjectRoute() {
  const { remedyId } = Route.useParams();
  const { obj } = Route.useSearch();
  const storedChatOpen = useStoredRemedyChatOpen(true);

  const { parentObject, scanObjectMap, objects, errorMessage } = Route.useLoaderData();

  if (errorMessage) {
    return (
      <div className="flex size-full items-center justify-center">
        <div className="flex flex-col items-center justify-center gap-2 text-center">
          <div className="flex items-center justify-center rounded-lg bg-tessera-primary-2 p-2">
            <AlertTriangle className="size-8 text-white" />
          </div>
          <div className="flex flex-col gap-2">
            <h2 className="text-lg font-medium text-white">Error</h2>
            <p className="text-sm text-gray-400">{errorMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!objects?.length) {
    return (
      <div className="flex size-full items-center justify-center">
        <div className="flex flex-col items-center justify-center gap-2 text-center">
          <div className="flex items-center justify-center rounded-lg bg-tessera-primary-2 p-2">
            <Box className="size-8 text-white" />
          </div>
          <div className="flex flex-col gap-2">
            <h2 className="text-lg font-medium text-white">No objects found</h2>
            <p className="text-sm text-gray-400">
              This object has no parent or sub objects. Please select a different object.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!parentObject) {
    return (
      <div className="flex size-full items-center justify-center">
        <div className="flex flex-col items-center justify-center gap-2 text-center">
          <div className="flex items-center justify-center rounded-lg bg-tessera-primary-2 p-2">
            <Box className="size-8 text-white" />
          </div>
          <div className="flex flex-col gap-2">
            <h2 className="text-lg font-medium text-white">No parent object found</h2>
            <p className="text-sm text-gray-400">This object has no parent object. Please select a different object.</p>
          </div>
        </div>
      </div>
    );
  }

  const selectedObject = objects.find((object) => object.name === obj || object.object_id === obj) ?? objects[0];

  return (
    <ObjectRemediationStoreProvider
      key={`${remedyId}-${parentObject.object_id}`}
      initialState={{
        remedyId,
        parentObject,
        objects,
        scanObjectMap,
        selectedObject,
        updatingStaticFixDiagnostics: true,
        isRemedyChatOpen: storedChatOpen,
      }}
    >
      <ObjectRemedyPage remedyId={remedyId} parentObjectId={selectedObject.object_id} />
    </ObjectRemediationStoreProvider>
  );
}
